/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, useLocation, type RouteObject } from 'react-router-dom';
import { ErrorBoundary, MatrixLoader } from '@shared/ui';
import { RequireAuth } from '@shared/auth';
import { PostHogPageviewTracker } from '@shared/analytics';
import { RouteErrorFallback } from './AppErrorBoundary';

/**
 * Single source of truth for routes.
 *
 * - Routes are data, not JSX trees. Lets breadcrumb, role-gating, and
 *   active-nav highlighting all read the route table without duplication.
 * - Every leaf is lazy-loaded so the initial bundle stays the auth screen
 *   plus the shell — no kitchen-sink bundle.
 * - Each feature owns its route component under `features/<slice>/routes/`;
 *   the router only knows the path → import mapping.
 *
 * Auth guard (issue #80):
 *   All /organizations/*, /account/*, and /dashboard/* routes are wrapped in a
 *   <RequireAuth> layout route. When `useAuth().status` is 'loading' the guard
 *   shows the same MatrixLoader spinner; when 'unauthenticated' it redirects
 *   to /auth. Auth routes (/auth/*) and public routes (/, /accept-invite) are
 *   intentionally outside the guard.
 */

const SignInRoute = lazy(() => import('@features/auth/routes/SignInRoute'));
const SignUpRoute = lazy(() => import('@features/auth/routes/SignUpRoute'));
const VerifyRoute = lazy(() => import('@features/auth/routes/VerifyRoute'));
const ForgotRoute = lazy(() => import('@features/auth/routes/ForgotRoute'));
const ResetRoute = lazy(() => import('@features/auth/routes/ResetRoute'));
const MagicLinkSentRoute = lazy(() => import('@features/auth/routes/MagicLinkSentRoute'));
const LockedRoute = lazy(() => import('@features/auth/routes/LockedRoute'));
const MagicLinkVerifyRoute = lazy(() => import('@features/auth/routes/MagicLinkVerifyRoute'));
const OAuthCallbackRoute = lazy(() => import('@features/auth/routes/OAuthCallbackRoute'));
const DeviceApprovalRoute = lazy(() => import('@features/auth/routes/DeviceApprovalRoute'));
const AccountAppearanceRoute = lazy(() => import('@features/account/routes/AppearanceRoute'));
const AccountAuthenticationRoute = lazy(
  () => import('@features/account/routes/AuthenticationRoute')
);
const AccountNotificationsRoute = lazy(() => import('@features/account/routes/NotificationsRoute'));
const AccountProfileRoute = lazy(() => import('@features/account/routes/ProfileRoute'));
const AccountSessionsRoute = lazy(() => import('@features/account/routes/SessionsRoute'));
const OrganizationsRoute = lazy(() => import('@features/organizations'));
const CreateOrgRoute = lazy(() => import('@features/organizations/routes/CreateOrgRoute'));
const DashboardHomeRoute = lazy(() => import('@features/dashboard'));
const AgentRegistryRoute = lazy(() =>
  import('@features/agents/routes/AgentRegistryRoute').then((m) => ({
    default: m.AgentRegistryRoute,
  }))
);
const AddAgentRoute = lazy(() =>
  import('@features/agents/routes/AddAgentRoute').then((m) => ({ default: m.AddAgentRoute }))
);
const AgentDetailRoute = lazy(() =>
  import('@features/agents/routes/AgentDetailRoute').then((m) => ({ default: m.AgentDetailRoute }))
);
const EditAgentRoute = lazy(() =>
  import('@features/agents/routes/EditAgentRoute').then((m) => ({ default: m.EditAgentRoute }))
);
const AgentFindRoute = lazy(() =>
  import('@features/agents/routes/AgentFindRoute').then((m) => ({ default: m.AgentFindRoute }))
);
const McpRegistryRoute = lazy(() =>
  import('@features/mcp/routes/McpRegistryRoute').then((m) => ({ default: m.McpRegistryRoute }))
);
const AddMcpServerRoute = lazy(() =>
  import('@features/mcp/routes/AddMcpServerRoute').then((m) => ({ default: m.AddMcpServerRoute }))
);
const McpBrowseRoute = lazy(() =>
  import('@features/mcp/routes/McpBrowseRoute').then((m) => ({ default: m.McpBrowseRoute }))
);
const McpBrowseDetailRoute = lazy(() =>
  import('@features/mcp/routes/McpBrowseDetailRoute').then((m) => ({
    default: m.McpBrowseDetailRoute,
  }))
);
const McpFindRoute = lazy(() =>
  import('@features/mcp/routes/McpFindRoute').then((m) => ({ default: m.McpFindRoute }))
);
const McpDetailRoute = lazy(() =>
  import('@features/mcp/routes/McpDetailRoute').then((m) => ({ default: m.McpDetailRoute }))
);
const ToolCatalogRoute = lazy(() =>
  import('@features/mcp/routes/ToolCatalogRoute').then((m) => ({ default: m.ToolCatalogRoute }))
);
const VulnerabilitiesRoute = lazy(() =>
  import('@features/mcp/routes/VulnerabilitiesRoute').then((m) => ({
    default: m.VulnerabilitiesRoute,
  }))
);
const ClientsRoute = lazy(() =>
  import('@features/mcp/routes/ClientsRoute').then((m) => ({ default: m.ClientsRoute }))
);
const CertificatesRoute = lazy(() =>
  import('@features/mcp/routes/CertificatesRoute').then((m) => ({ default: m.CertificatesRoute }))
);
const AuditRoute = lazy(() =>
  import('@features/audit/routes/AuditRoute').then((m) => ({ default: m.AuditRoute }))
);
const GraphRoute = lazy(() =>
  import('@features/graph/routes/GraphRoute').then((m) => ({ default: m.GraphRoute }))
);
const MembersRoute = lazy(() =>
  import('@features/team/routes/MembersRoute').then((m) => ({ default: m.MembersRoute }))
);
const InviteRoute = lazy(() =>
  import('@features/team/routes/InviteRoute').then((m) => ({ default: m.InviteRoute }))
);
const MemberDetailRoute = lazy(() =>
  import('@features/team/routes/MemberDetailRoute').then((m) => ({ default: m.MemberDetailRoute }))
);
const InvitesRoute = lazy(() =>
  import('@features/team/routes/InvitesRoute').then((m) => ({ default: m.InvitesRoute }))
);
const TeamSettingsRoute = lazy(() =>
  import('@features/team/routes/TeamSettingsRoute').then((m) => ({ default: m.TeamSettingsRoute }))
);
const JoinRequestsRoute = lazy(() =>
  import('@features/team/routes/JoinRequestsRoute').then((m) => ({ default: m.JoinRequestsRoute }))
);
const AcceptInviteRoute = lazy(() => import('@features/team/routes/AcceptInviteRoute'));
const RolesRoute = lazy(() =>
  import('@features/team/routes/RolesRoute').then((m) => ({ default: m.RolesRoute }))
);
const RoleDetailRoute = lazy(() =>
  import('@features/team/routes/RoleDetailRoute').then((m) => ({ default: m.RoleDetailRoute }))
);
const RoleEditorRoute = lazy(() =>
  import('@features/team/routes/RoleEditorRoute').then((m) => ({ default: m.RoleEditorRoute }))
);
// Eagerly imported (tiny, no JSX cost): wraps the three role routes so they
// share the same RolesProvider instance and thus the same in-memory store.
import { RolesProvider } from '@features/team/hooks/use-roles-store';
const PoliciesRoute = lazy(() => import('@features/policies/routes/PoliciesRoute'));
const PolicyEditorRoute = lazy(() => import('@features/policies/routes/PolicyEditorRoute'));
const PolicyComposeRoute = lazy(() => import('@features/policies/routes/PolicyComposeRoute'));
const PolicyTemplatesRoute = lazy(() => import('@features/policies/routes/PolicyTemplatesRoute'));
const PolicyDetailRoute = lazy(() => import('@features/policies/routes/PolicyDetailRoute'));
const PlansRoute = lazy(() =>
  import('@features/plans/routes/PlansRoute').then((m) => ({ default: m.PlansRoute }))
);
const PlanDetailRoute = lazy(() =>
  import('@features/plans/routes/PlanDetailRoute').then((m) => ({ default: m.PlanDetailRoute }))
);
// Held Actions — the original Intent Plans queue (list → side-modal peek →
// full-page IAP detail). Kept as its own destination alongside the newer
// Plans experience.
const IntentPlansRoute = lazy(() =>
  import('@features/intent-plans/routes/IntentPlansRoute').then((m) => ({
    default: m.IntentPlansRoute,
  }))
);
const IntentPlanDetailRoute = lazy(() =>
  import('@features/intent-plans/routes/IntentPlanDetailRoute').then((m) => ({
    default: m.IntentPlanDetailRoute,
  }))
);
const ApiUsageRoute = lazy(() =>
  import('@features/api-portal/routes/ApiUsageRoute').then((m) => ({ default: m.ApiUsageRoute }))
);
const ApiKeysRoute = lazy(() =>
  import('@features/api-portal/routes/ApiKeysRoute').then((m) => ({ default: m.ApiKeysRoute }))
);
const GatewayConnectRoute = lazy(() =>
  import('@features/api-portal/routes/GatewayConnectRoute').then((m) => ({
    default: m.GatewayConnectRoute,
  }))
);
const ApiLogsRoute = lazy(() =>
  import('@features/api-portal/routes/ApiLogsRoute').then((m) => ({ default: m.ApiLogsRoute }))
);
const PolicyEngineRoute = lazy(() =>
  import('@features/settings/routes/PolicyEngineRoute').then((m) => ({
    default: m.PolicyEngineRoute,
  }))
);
const IdentityRoute = lazy(() =>
  import('@features/settings/routes/IdentityRoute').then((m) => ({ default: m.IdentityRoute }))
);
const SsoSettingsRoute = lazy(() =>
  import('@features/settings/routes/SsoSettingsRoute').then((m) => ({
    default: m.SsoSettingsRoute,
  }))
);
const PreferencesRoute = lazy(() =>
  import('@features/settings/routes/PreferencesRoute').then((m) => ({
    default: m.PreferencesRoute,
  }))
);
const ObservabilitySettingsRoute = lazy(() =>
  import('@features/observability/routes/ObservabilitySettingsRoute').then((m) => ({
    default: m.ObservabilitySettingsRoute,
  }))
);
// Activity feature slice — feed + quick scan + scan report.
const ActivityRoute = lazy(() =>
  import('@features/activity/routes/ActivityRoute').then((m) => ({ default: m.ActivityRoute }))
);
const ScanRunRoute = lazy(() =>
  import('@features/activity/routes/ScanRunRoute').then((m) => ({ default: m.ScanRunRoute }))
);

const ScanReportRoute = lazy(() =>
  import('@features/activity/routes/ScanReportRoute').then((m) => ({ default: m.ScanReportRoute }))
);
// Scan-engine surfaces (native rule engine, ScanRun/Finding backed).
const ScanHistoryRoute = lazy(() => import('@features/scans/routes/ScanHistoryRoute'));
const EngineScanReportRoute = lazy(() => import('@features/scans/routes/ScanReportRoute'));
const AuditLogRoute = lazy(() =>
  import('@features/activity/routes/AuditLogRoute').then((m) => ({ default: m.AuditLogRoute }))
);
// Observability feature slice — SDK -> backend -> UI telemetry pipeline
// (sessions, traces, spans). Static sub-paths (`/traces`) must precede the
// dynamic `/sessions/:id` and `/traces/:id` detail routes below.
const ObservabilityHomeRoute = lazy(() =>
  import('@features/observability/routes/ObservabilityHomeRoute').then((m) => ({
    default: m.ObservabilityHomeRoute,
  }))
);
const SessionDetailRoute = lazy(() =>
  import('@features/observability/routes/SessionDetailRoute').then((m) => ({
    default: m.SessionDetailRoute,
  }))
);
const TraceListRoute = lazy(() =>
  import('@features/observability/routes/TraceListRoute').then((m) => ({
    default: m.TraceListRoute,
  }))
);
const TraceFindRoute = lazy(() =>
  import('@features/observability/routes/TraceFindRoute').then((m) => ({
    default: m.TraceFindRoute,
  }))
);
const TraceDetailRoute = lazy(() =>
  import('@features/observability/routes/TraceDetailRoute').then((m) => ({
    default: m.TraceDetailRoute,
  }))
);
const ErrorExplorerRoute = lazy(() =>
  import('@features/observability/routes/ErrorExplorerRoute').then((m) => ({
    default: m.ErrorExplorerRoute,
  }))
);
const PolicyInsightsRoute = lazy(() =>
  import('@features/observability/routes/PolicyInsightsRoute').then((m) => ({
    default: m.PolicyInsightsRoute,
  }))
);

function PageFallback() {
  return (
    <div className="text-aq-accent flex min-h-screen items-center justify-center">
      <MatrixLoader pattern="wave" size="md" />
    </div>
  );
}

/**
 * Every route element goes through here, so this is where the crash guard
 * belongs: one wrapper protects all of them.
 *
 * The boundary sits OUTSIDE <Suspense> deliberately. A failed `lazy()` import
 * (the "Failed to fetch dynamically imported module" case after a redeploy or a
 * corrupted dev-server watcher) rejects the lazy promise, and only a boundary
 * above Suspense can catch that. Inside, it would never see it and the user
 * would get the blank screen this exists to prevent.
 *
 * `resetKeys` on the pathname means navigating away clears a stuck crash
 * instead of stranding the user on the fallback.
 */
function withSuspense(node: React.ReactNode) {
  return (
    <RouteCrashGuard>
      <Suspense fallback={<PageFallback />}>{node}</Suspense>
    </RouteCrashGuard>
  );
}

/**
 * Route-scoped crash boundary. Split out as a component so it can read the
 * current location (hooks are unavailable inside the class boundary itself).
 */
function RouteCrashGuard({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary scope="route" noun="this page" resetKeys={[pathname]}>
      {children}
    </ErrorBoundary>
  );
}

/**
 * Wraps a team-roles route element with the slice's RolesProvider so the
 * three roles routes (catalog, detail, editor) share one in-memory store and
 * see each other's mutations without a page reload.
 */
function withRoles(node: React.ReactNode) {
  return <RolesProvider>{withSuspense(node)}</RolesProvider>;
}

export const routes: RouteObject[] = [
  {
    element: <PostHogPageviewTracker />,
    errorElement: <RouteErrorFallback />,
    children: [
      { path: '/', element: <Navigate to="/auth" replace /> },
      { path: '/auth', element: withSuspense(<SignInRoute />) },
      { path: '/auth/signup', element: withSuspense(<SignUpRoute />) },
      { path: '/auth/verify', element: withSuspense(<VerifyRoute />) },
      { path: '/auth/forgot', element: withSuspense(<ForgotRoute />) },
      { path: '/auth/reset', element: withSuspense(<ResetRoute />) },
      { path: '/auth/magic-link/verify', element: withSuspense(<MagicLinkVerifyRoute />) },
      { path: '/auth/magic-link', element: withSuspense(<MagicLinkSentRoute />) },
      { path: '/auth/locked', element: withSuspense(<LockedRoute />) },
      { path: '/auth/callback', element: withSuspense(<OAuthCallbackRoute />) },
      { path: '/auth/device', element: withSuspense(<DeviceApprovalRoute />) },
      // Public invite acceptance — no dashboard shell; lands on the dashboard on success.
      { path: '/accept-invite', element: withSuspense(<AcceptInviteRoute />) },

      /* ─── protected routes (RequireAuth gate, issue #80) ─── */
      {
        element: <RequireAuth />,
        children: [
          { path: '/organizations', element: withSuspense(<OrganizationsRoute />) },
          { path: '/organizations/new', element: withSuspense(<CreateOrgRoute />) },
          // Account feature slice — user-level settings. Lives OUTSIDE /dashboard
          // because account settings are user-scoped, not org-scoped.
          { path: '/account', element: withSuspense(<AccountProfileRoute />) },
          {
            path: '/account/authentication',
            element: withSuspense(<AccountAuthenticationRoute />),
          },
          { path: '/account/sessions', element: withSuspense(<AccountSessionsRoute />) },
          { path: '/account/notifications', element: withSuspense(<AccountNotificationsRoute />) },
          { path: '/account/appearance', element: withSuspense(<AccountAppearanceRoute />) },
          { path: '/dashboard', element: withSuspense(<DashboardHomeRoute />) },
          {
            path: '/dashboard/agents',
            element: withSuspense(<AgentRegistryRoute />),
          },
          {
            path: '/dashboard/agents/add',
            element: withSuspense(<AddAgentRoute />),
          },
          // Static literal before `:slug` so it is not captured as a slug.
          {
            path: '/dashboard/agents/find',
            element: withSuspense(<AgentFindRoute />),
          },
          {
            path: '/dashboard/agents/:agentId/edit',
            element: withSuspense(<EditAgentRoute />),
          },
          // Agent detail. ORDER MATTERS: must follow `/add` and `/:agentId/edit` so
          // literal paths aren't captured as a slug.
          {
            path: '/dashboard/agents/:slug',
            element: withSuspense(<AgentDetailRoute />),
          },
          // MCP feature slice. ORDER MATTERS: static sub-paths must precede `:slug`,
          // otherwise react-router treats `tool-catalog` etc. as slug values.
          { path: '/dashboard/mcp', element: withSuspense(<McpRegistryRoute />) },
          { path: '/dashboard/mcp/add', element: withSuspense(<AddMcpServerRoute />) },
          // Browse + Find — static literals before dynamic `:slug` to avoid capture.
          { path: '/dashboard/mcp/browse', element: withSuspense(<McpBrowseRoute />) },
          {
            path: '/dashboard/mcp/browse/:registryId',
            element: withSuspense(<McpBrowseDetailRoute />),
          },
          { path: '/dashboard/mcp/find', element: withSuspense(<McpFindRoute />) },
          { path: '/dashboard/mcp/tool-catalog', element: withSuspense(<ToolCatalogRoute />) },
          {
            path: '/dashboard/mcp/vulnerabilities',
            element: withSuspense(<VulnerabilitiesRoute />),
          },
          { path: '/dashboard/mcp/clients', element: withSuspense(<ClientsRoute />) },
          { path: '/dashboard/mcp/certificates', element: withSuspense(<CertificatesRoute />) },
          { path: '/dashboard/mcp/:slug', element: withSuspense(<McpDetailRoute />) },
          // Audit feature slice.
          { path: '/dashboard/audit', element: withSuspense(<AuditRoute />) },
          // AIQ Graph — the interactive runtime graph of the agentic estate.
          { path: '/dashboard/graph', element: withSuspense(<GraphRoute />) },
          // Team feature slice.
          // ORDER MATTERS: every static `/team/...` path must precede the dynamic
          // `/team/:slug` member-detail route so it doesn't swallow `invite`,
          // `invites`, or `roles` as a member slug.
          { path: '/dashboard/team', element: withSuspense(<MembersRoute />) },
          { path: '/dashboard/team/invite', element: withSuspense(<InviteRoute />) },
          { path: '/dashboard/team/invites', element: withSuspense(<InvitesRoute />) },
          { path: '/dashboard/team/settings', element: withSuspense(<TeamSettingsRoute />) },
          { path: '/dashboard/team/join-requests', element: withSuspense(<JoinRequestsRoute />) },
          { path: '/dashboard/team/roles', element: withRoles(<RolesRoute />) },
          { path: '/dashboard/team/roles/new', element: withRoles(<RoleEditorRoute />) },
          { path: '/dashboard/team/roles/:roleKey', element: withRoles(<RoleDetailRoute />) },
          { path: '/dashboard/team/roles/:roleKey/edit', element: withRoles(<RoleEditorRoute />) },
          // Dynamic member detail — LAST so the static paths above win.
          { path: '/dashboard/team/:slug', element: withSuspense(<MemberDetailRoute />) },
          // Policies feature slice. Static paths precede `:id` so react-router does not
          // treat them as policy IDs.
          { path: '/dashboard/policies', element: withSuspense(<PoliciesRoute />) },
          { path: '/dashboard/policies/compose', element: withSuspense(<PolicyComposeRoute />) },
          {
            path: '/dashboard/policies/templates',
            element: withSuspense(<PolicyTemplatesRoute />),
          },
          { path: '/dashboard/policies/new', element: withSuspense(<PolicyEditorRoute />) },
          { path: '/dashboard/policies/:id/edit', element: withSuspense(<PolicyEditorRoute />) },
          { path: '/dashboard/policies/:id', element: withSuspense(<PolicyDetailRoute />) },
          // Plans feature slice (V3) — the Plan experience. Own-route tabs under
          // `/plans/:id/:tab`.
          // ORDER MATTERS: `/plans/:id/:tab` is a distinct depth from `/plans/:id`.
          { path: '/dashboard/plans', element: withSuspense(<PlansRoute />) },
          { path: '/dashboard/plans/:id', element: withSuspense(<PlanDetailRoute />) },
          { path: '/dashboard/plans/:id/:tab', element: withSuspense(<PlanDetailRoute />) },
          // Held Actions — the Intent Plans queue + its full-page IAP detail.
          { path: '/dashboard/intent-plans', element: withSuspense(<IntentPlansRoute />) },
          { path: '/dashboard/intent-plans/:id', element: withSuspense(<IntentPlanDetailRoute />) },
          { path: '/dashboard/gateway', element: withSuspense(<GatewayConnectRoute />) },
          {
            path: '/dashboard/connections',
            element: <Navigate to="/dashboard/gateway" replace />,
          },
          {
            path: '/dashboard/api/connect',
            element: <Navigate to="/dashboard/gateway" replace />,
          },
          {
            path: '/dashboard/agents/connect',
            element: <Navigate to="/dashboard/gateway" replace />,
          },
          // API portal feature slice (Developer → API).
          { path: '/dashboard/api', element: withSuspense(<ApiUsageRoute />) },
          { path: '/dashboard/api/keys', element: withSuspense(<ApiKeysRoute />) },
          { path: '/dashboard/api/logs', element: withSuspense(<ApiLogsRoute />) },
          // Settings feature slice: Policy Engine, Agent identity, Single sign-on, Preferences.
          {
            path: '/dashboard/settings',
            element: <Navigate to="/dashboard/settings/engine" replace />,
          },
          { path: '/dashboard/settings/engine', element: withSuspense(<PolicyEngineRoute />) },
          { path: '/dashboard/settings/identity', element: withSuspense(<IdentityRoute />) },
          { path: '/dashboard/settings/sso', element: withSuspense(<SsoSettingsRoute />) },
          { path: '/dashboard/settings/preferences', element: withSuspense(<PreferencesRoute />) },
          {
            path: '/dashboard/settings/observability',
            element: withSuspense(<ObservabilitySettingsRoute />),
          },
          // Activity feature slice. Quick-scan report path comes last so the static
          // `/scan` and `/activity` segments are matched ahead of any future params.
          // Audit Log — IAP audit sub-page. Static path must precede the
          // /dashboard/activity leaf so it isn't captured.
          { path: '/dashboard/activity/audit', element: withSuspense(<AuditLogRoute />) },
          { path: '/dashboard/activity', element: withSuspense(<ActivityRoute />) },
          { path: '/dashboard/scan', element: withSuspense(<ScanRunRoute />) },
          { path: '/dashboard/scan/:reportId', element: withSuspense(<ScanReportRoute />) },
          // Plural paths are the scan-engine surfaces. Static path first so the
          // history list is not captured by the :runId leaf.
          { path: '/dashboard/scans', element: withSuspense(<ScanHistoryRoute />) },
          {
            path: '/dashboard/scans/:runId',
            element: withSuspense(<EngineScanReportRoute />),
          },
          // Observability feature slice. ORDER MATTERS: `/traces` and
          // `/traces/find` (static) must precede `/sessions/:id` and
          // `/traces/:id` (dynamic) so a literal segment is never captured as
          // an id param.
          { path: '/dashboard/observability', element: withSuspense(<ObservabilityHomeRoute />) },
          { path: '/dashboard/observability/traces', element: withSuspense(<TraceListRoute />) },
          {
            path: '/dashboard/observability/errors',
            element: withSuspense(<ErrorExplorerRoute />),
          },
          {
            path: '/dashboard/observability/policy-insights',
            element: withSuspense(<PolicyInsightsRoute />),
          },
          {
            path: '/dashboard/observability/traces/find',
            element: withSuspense(<TraceFindRoute />),
          },
          {
            path: '/dashboard/observability/sessions/:id',
            element: withSuspense(<SessionDetailRoute />),
          },
          {
            path: '/dashboard/observability/traces/:id',
            element: withSuspense(<TraceDetailRoute />),
          },
        ],
      },
      // NOTE: the old `/dashboard/agents/capabilities` and `/dashboard/agents/owners`
      // routes were retired in iteration-4 — those views now exist as filter chips
      // on the registry. Deep links to those URLs fall through to the catch-all
      // below, which redirects to `/auth` (acceptable for the prototype).
      { path: '*', element: <Navigate to="/auth" replace /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
